import React from "react";
import AuthGuard from "./AuthGuard";
import SettingsLayout from "./SettingsLayout";
import SubscriptionsLayout from "./SubscriptionsLayout";
import { useRoutes, Navigate } from "react-router-dom";
import ToolkitStudyLayout from "../components/toolkit/components/test-study/StudyLayout";
import {
  Products,
  NewTest,
  Error404,
  Template,
  Dashboard,
  Response,
  EndScreen,
  StartScreen,
  TestReport,
  TestPreview,
  TestCreation,
  AnalysisSpace,
  BrowserTest,
  EmbedViewer,
  EditProfile,
  Billings,
  Subscriptions,
  Integrations,
  GlobalSettings,
  UserOnboarding,
  People,
  Workspace,
  Organization,
  WorkspaceSwitch,
  WorkspaceInvitation,
  // DashboardCommon,
  DashboardModerated,
  Heatmap,
  Plans,
  UpgradePlan,
  TopUp,
  ToolkitDashboard,
  ToolkitTestCreation,
  ToolkitTestCreationInit,
  ToolkitTestReport,
  ToolkitTestReportPieTree,
  ToolkitTestStudy,
  ToolkitCardSortBegin,
  ToolkitTreeStudyBegin,
  // ToolkitParticipateStudyBegin,
  ToolkitStudyInterrupted,
  ToolkitQuestionnaireBegin,
  ToolkitQuestionnaireThankYou,
  NavigationTree,
} from "./elements";

export default function Router() {
  return useRoutes([
    {
      path: "/",
      element: <AuthGuard fetchBalances />,
      children: [
        { element: <Products />, index: true },
        { path: "dashboard", element: <Dashboard /> },
        { path: "surveys", element: <Dashboard isSurveys={true} /> },
        { path: "templates", element: <Template /> },
        { path: "template/:templateId", element: <Template /> },
        { path: "project-analysis", element: <AnalysisSpace /> },
        { path: "project-analysis/:projectId", element: <AnalysisSpace /> },
        { path: "moderated", element: <DashboardModerated /> },
        { path: "moderated/:createNewProject", element: <Navigate to='/moderated' /> },
        { path: "moderated/:projectId/edit", element: <Navigate to='/moderated' /> },
        { path: "moderated/:projectId/report", element: <DashboardModerated isReport /> },
        { path: "onboarding", element: <UserOnboarding /> },
        {
          path: "test",
          children: [
            { path: "new", element: <NewTest /> },
            { path: ":draftId", element: <TestCreation /> },
            { path: ":draftId/:activeTab", element: <TestCreation /> },
          ],
        },
        { path: "workspace/:id", element: <WorkspaceSwitch /> },
        { path: "invites/:resourceType/:resourceId/:uuid", element: <WorkspaceInvitation /> },
      ],
    },
    {
      path: "plans",
      element: <SubscriptionsLayout />,
      children: [
        { element: <Plans />, index: true },
        { path: "upgrade/:planId/:recurringType", element: <UpgradePlan /> },
        { path: "topup", element: <TopUp /> },
      ],
    },
    {
      path: "response",
      children: [
        { element: <Response />, index: true },
        { path: "end", element: <EndScreen /> },
        { path: "start", element: <StartScreen /> },
        { path: "preview/:draftId", element: <Response /> },
        { path: ":setId", element: <Response /> },
        { path: ":setId/:userId", element: <Response /> },
      ],
    },
    {
      path: "user",
      element: <SettingsLayout />,
      children: [
        { path: "edit-profile", element: <EditProfile /> },
        { path: "billings", element: <Billings /> },
        { path: "subscriptions", element: <Subscriptions /> },
        { path: "integrations", element: <Integrations /> },
        { path: "global-settings", element: <GlobalSettings /> },
        { path: "organization", element: <Organization /> },
        { path: "organization/people", element: <People /> },
        { path: "organization/workspace", element: <Workspace /> },
      ],
    },
    // Toolkit routes start
    {
      element: <AuthGuard fetchProjects />,
      path: "toolkit",
      children: [
        { element: <ToolkitDashboard />, index: true },
        { path: ":menuType", element: <ToolkitDashboard /> },
      ],
    },
    {
      element: <AuthGuard fetchProjects />,
      path: "tests",
      children: [
        { path: ":testId", element: <ToolkitTestCreation /> },
        { path: ":testId/:action", element: <ToolkitTestCreation /> },
        { path: "new/:testType", element: <ToolkitTestCreationInit /> },
      ],
    },
    {
      element: <AuthGuard />,
      path: "tests/report/",
      children: [
        { path: ":testId", element: <ToolkitTestReport /> },
        { path: ":testId/pie-tree/:activeTaskIndex", element: <ToolkitTestReportPieTree /> },
        { path: ":testId/pie-tree/:activeTaskIndex/:isNewPage", element: <ToolkitTestReportPieTree /> },
      ],
    },
    {
      path: "toolkit/share/report",
      children: [
        { path: ":testId", element: <ToolkitTestReport isSharedReport /> },
        { path: ":testId/pie-tree/:activeTaskIndex", element: <ToolkitTestReportPieTree isSharedReport /> },
        { path: ":testId/pie-tree/:activeTaskIndex/:isNewPage", element: <ToolkitTestReportPieTree isSharedReport /> },
      ],
    },
    {
      element: <ToolkitStudyLayout />,
      path: "study",
      children: [
        { element: <ToolkitTestStudy />, index: true },
        { path: ":testId", element: <ToolkitTestStudy /> },
        { path: ":testId/:action", element: <ToolkitTestStudy /> },
        { path: "treebegin", element: <ToolkitTreeStudyBegin /> },
        { path: "cardsortingbegin", element: <ToolkitCardSortBegin /> },
        // { path: "participatebegin", element: <ToolkitParticipateStudyBegin /> },
        { path: "interrupted", element: <ToolkitStudyInterrupted /> },
        { path: "questionnaire/thank-you", element: <ToolkitQuestionnaireThankYou /> },
        { path: "questionnaire/thank-you/:complete", element: <ToolkitQuestionnaireThankYou /> },
        { path: "questionnaire/:type", element: <ToolkitQuestionnaireBegin /> },
      ],
    },
    // Toolkit routes end
    { path: "browser-test/:testId", element: <BrowserTest /> },
    { path: "embedViewer", element: <EmbedViewer /> },
    { path: "preview/:testId", element: <TestPreview /> },
    { path: "share/report/:testKey", element: <TestReport /> },
    { path: "/test/:draftId/heatmap", element: <Heatmap /> },
    { path: "/test/:draftId/heatmap/:taskId", element: <Heatmap /> },
    { path: "/test/:draftId/navtree/:taskId", element: <NavigationTree /> },
    { path: "/share/:testKey/navtree/:taskId", element: <NavigationTree /> },
    { path: "/share/heatmap/:testKey", element: <Heatmap /> },
    { path: "/share/heatmap/:testKey/:taskId", element: <Heatmap /> },
    { path: "*", element: <Error404 /> },
  ]);
}
